import { useEffect, useState } from 'react';
import { Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@material-ui/core';
import { Link, Progress, WarningPanel } from '@backstage/core-components';
import { useReleasesCall } from './useReleases';

type Changes = {
  commits: number;
  authors: string[];
  tickets: string[];
  prs: number[];
  areas: { area: string; files: string[] }[];
  endpoints: { added: string[]; removed: string[] };
  envVars: string[];
  dependencies: { added: string[]; removed: string[]; updated: string[] };
  risks: string[];
  codeChanged: boolean;
  truncated: boolean;
  url?: string;
};

const Section = ({ title, items, mono }: { title: string; items: string[]; mono?: boolean }) =>
  items.length ? (
    <div style={{ marginTop: 12 }}>
      <Typography variant="subtitle2">{title}</Typography>
      <ul style={{ margin: '4px 0', paddingLeft: 20 }}>
        {items.map(i => (
          <li key={i} style={mono ? { fontFamily: 'monospace', fontSize: 13 } : undefined}>
            {i}
          </li>
        ))}
      </ul>
    </div>
  ) : null;

/** "Qué cambia" entre dos versiones (prod…qa): resumen sin IA (endpoints, variables, dependencias, BD, tickets) y el diff. */
export const ChangesButton = ({ slug, base, head }: { slug: string; base: string; head: string }) => {
  const call = useReleasesCall();
  const [open, setOpen] = useState(false);
  const [data, setData] = useState<Changes>();
  const [error, setError] = useState<string>();
  useEffect(() => {
    if (!open || data) return;
    call(`/changes?slug=${encodeURIComponent(slug)}&base=${encodeURIComponent(base)}&head=${encodeURIComponent(head)}`).then(setData, e =>
      setError(String(e?.message ?? e)),
    );
  }, [open, data, call, slug, base, head]);
  const repo = `https://github.com/${slug}`;

  return (
    <>
      <Button size="small" color="primary" onClick={() => setOpen(true)}>
        Qué cambia
      </Button>
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {slug.split('/')[1]}: {base} → {head}
        </DialogTitle>
        <DialogContent dividers>
          {error && <WarningPanel title="No se pudo leer el cambio" message={error} />}
          {!error && !data && <Progress />}
          {data && (
            <>
              <Typography variant="body2" color="textSecondary">
                {data.commits} commits de {data.authors.join(', ') || '—'}
                {data.truncated ? ' · el diff es muy grande: el resumen puede estar incompleto' : ''}
              </Typography>
              {data.risks.length > 0 && (
                <WarningPanel severity={data.codeChanged ? 'warning' : 'info'} title="A revisar antes de prod">
                  <ul style={{ margin: 0, paddingLeft: 20 }}>
                    {data.risks.map(r => (
                      <li key={r}>{r}</li>
                    ))}
                  </ul>
                </WarningPanel>
              )}
              {(data.tickets.length > 0 || data.prs.length > 0) && (
                <div style={{ marginTop: 12 }}>
                  {data.tickets.map(t => (
                    <Chip key={t} size="small" label={t} style={{ marginRight: 4 }} />
                  ))}
                  {data.prs.map(n => (
                    <Link key={n} to={`${repo}/pull/${n}`} style={{ marginRight: 8 }}>
                      #{n}
                    </Link>
                  ))}
                </div>
              )}
              <Section title="Áreas" items={data.areas.map(a => `${a.area}: ${a.files.length} archivo(s)`)} />
              <Section title="Endpoints nuevos" items={data.endpoints.added} mono />
              <Section title="Endpoints quitados o cambiados" items={data.endpoints.removed} mono />
              <Section title="Variables de entorno nuevas" items={data.envVars} mono />
              <Section title="Dependencias nuevas" items={data.dependencies.added} mono />
              <Section title="Dependencias quitadas" items={data.dependencies.removed} mono />
              <Section title="Dependencias actualizadas" items={data.dependencies.updated} mono />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Link to={data?.url ?? `${repo}/compare/${base}...${head}`}>Ver el diff en GitHub</Link>
          <Button onClick={() => setOpen(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
